// btw-front/src/components/admin/dashboard/StaleDealsList.jsx
// [관리 대상 딜] 목록 — 데스크탑 테이블(정렬) + 모바일 카드, 행 클릭 이동, 단건 안내 발송
// props 는 StaleDealsTab 의 상태/핸들러 이름을 그대로 받는다(부모 식별자와 1:1).
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "../../ui/Badge";
import { EmptyState } from "../../ui/EmptyState";
import { usePartnerSession } from "../../../context/PartnerSessionContext";
import { SortTh, dealClickPath, stallTone } from "./shared";

export default function StaleDealsList({
  data,
  days,
  isSearching,
  loading,
  noticeSendingId,
  sendNotice,
  sort,
  sortedItems,
  toggleSort,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = usePartnerSession();
  return (
    <>
      {/* 목록 (주인공) */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        </div>
      ) : data.items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <EmptyState
            title={isSearching ? t("admin.staleDeals.searchEmpty") : t("admin.staleDeals.empty")}
          />
        </div>
      ) : (
        <>
          {/* 데스크탑 테이블 — colgroup 으로 텍스트 3컬럼만 폭 배분, 나머지는 내용 최소폭+nowrap */}
          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white md:block">
            <table className="w-full text-sm">
              <colgroup>
                <col className="w-[15%]" /> {/* 파트너/등록 */}
                <col className="w-[26%]" /> {/* 프로젝트명 */}
                <col className="w-[21%]" /> {/* 고객사 */}
                <col className="w-px" /> {/* 영업대표 */}
                <col className="w-px" /> {/* 영업단계 */}
                <col className="w-px" /> {/* 마지막 변동 */}
                <col className="w-px" /> {/* 정체 일수 */}
                <col className="w-px" /> {/* 안내 메일 */}
              </colgroup>
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <SortTh
                    sort={sort}
                    onToggle={toggleSort}
                    sortKey="company"
                    label={t("admin.staleDeals.table.company")}
                  />
                  <th className="px-4 py-2.5 text-left font-medium">
                    {t("admin.staleDeals.table.projectName")}
                  </th>
                  <SortTh
                    sort={sort}
                    onToggle={toggleSort}
                    sortKey="customer"
                    label={t("admin.staleDeals.table.customer")}
                  />
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                    {t("admin.staleDeals.table.salesRep")}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                    {t("admin.staleDeals.table.status")}
                  </th>
                  <th className="whitespace-nowrap px-4 py-2.5 text-left font-medium">
                    {t("admin.staleDeals.table.lastUpdated")}
                  </th>
                  <SortTh
                    sort={sort}
                    onToggle={toggleSort}
                    sortKey="days"
                    label={t("admin.staleDeals.table.daysSince")}
                    align="right"
                  />
                  <th className="whitespace-nowrap px-4 py-2.5 text-right font-medium">
                    {t("admin.staleDeals.notice.colTitle")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedItems.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => navigate(dealClickPath(d, profile.userId))}
                    className="cursor-pointer transition hover:bg-slate-50"
                  >
                    <td className="break-words px-4 py-2.5 align-top text-slate-700">
                      {d.displayCompanyName || "-"}
                    </td>
                    <td className="break-words px-4 py-2.5 align-top font-medium text-slate-900">
                      {d.projectName}
                    </td>
                    <td className="break-words px-4 py-2.5 align-top text-slate-600">
                      {d.customerCompany}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 align-top text-slate-600">
                      {d.salesRepName || "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 align-top">
                      <Badge tone={d.status}>{t(`partners.deals.status.${d.status}`)}</Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 align-top text-slate-500">
                      {d.updatedAt ? d.updatedAt.slice(0, 10) : "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right align-top">
                      <span
                        className={`font-semibold tabular-nums ${stallTone(d.daysSinceUpdate)}`}
                      >
                        {t("admin.staleDeals.daysAgo", { days: d.daysSinceUpdate })}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right align-top">
                      {d.isNoticeSent ? (
                        <span className="text-xs text-slate-400">
                          {t("admin.staleDeals.notice.sent")}
                          {d.noticeSentAt ? ` ${String(d.noticeSentAt).slice(5)}` : ""}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            sendNotice(d);
                          }}
                          disabled={noticeSendingId === d.id}
                          className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          {noticeSendingId === d.id
                            ? t("admin.staleDeals.notice.sending")
                            : t("admin.staleDeals.notice.send")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 모바일 카드 — 안내 메일 버튼 중첩을 위해 button 대신 div+onClick */}
          <div className="grid gap-2 md:hidden">
            {sortedItems.map((d) => (
              <div
                key={d.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(dealClickPath(d, profile.userId))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") navigate(dealClickPath(d, profile.userId));
                }}
                className="flex cursor-pointer flex-col gap-1.5 rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-accent/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="min-w-0 break-words text-sm font-medium text-slate-900">
                    {d.projectName}
                  </span>
                  <span
                    className={`shrink-0 text-xs font-semibold tabular-nums ${stallTone(d.daysSinceUpdate)}`}
                  >
                    {t("admin.staleDeals.daysAgo", { days: d.daysSinceUpdate })}
                  </span>
                </div>
                <div className="truncate text-xs text-slate-600">{d.customerCompany}</div>
                <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                  <span className="min-w-0 truncate">
                    {d.displayCompanyName || "-"} · {d.salesRepName || "-"}
                  </span>
                  <Badge tone={d.status}>{t(`partners.deals.status.${d.status}`)}</Badge>
                </div>
                <div className="mt-1 flex justify-end">
                  {d.isNoticeSent ? (
                    <span className="text-xs text-slate-400">
                      {t("admin.staleDeals.notice.sent")}
                      {d.noticeSentAt ? ` ${String(d.noticeSentAt).slice(5)}` : ""}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        sendNotice(d);
                      }}
                      disabled={noticeSendingId === d.id}
                      className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                    >
                      {noticeSendingId === d.id
                        ? t("admin.staleDeals.notice.sending")
                        : t("admin.staleDeals.notice.send")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
